import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollReveal } from '@/components/ScrollReveal';
import { ArrowRight, LucideIcon } from 'lucide-react';
interface HeroBenefit {
  icon: LucideIcon;
  text: string;
}
interface HeroProps {
  badge?: {
    text: string;
    icon?: string;
  };
  title: string;
  titleHighlight?: string;
  description: string;
  benefits?: HeroBenefit[];
  primaryCTA: {
    text: string;
    href: string;
  };
  secondaryCTA?: {
    text: string;
    href: string;
  };
  image: {
    src: string;
    alt: string;
  };
  backgroundVariant?: 'default' | 'emerald' | 'slate' | 'amber';
}
const Hero = ({
  badge,
  title,
  titleHighlight,
  description,
  benefits,
  primaryCTA,
  secondaryCTA,
  image,
  backgroundVariant = 'default'
}: HeroProps) => {
  const getBackgroundClasses = () => {
    switch (backgroundVariant) {
      case 'emerald':
        return 'bg-gradient-to-br from-emerald-50 via-white to-emerald-100/30';
      case 'slate':
        return 'bg-gradient-to-br from-slate-50 via-white to-slate-100/30';
      case 'amber':
        return 'bg-gradient-to-br from-amber-50 via-white to-amber-100/30';
      default:
        return 'bg-gradient-to-br from-stone-50 via-white to-emerald-50/30';
    }
  };
  const getBlurColors = () => {
    switch (backgroundVariant) {
      case 'emerald':
        return {
          primary: 'bg-emerald-500',
          secondary: 'bg-emerald-300'
        };
      case 'slate':
        return {
          primary: 'bg-slate-500',
          secondary: 'bg-slate-300'
        };
      case 'amber':
        return {
          primary: 'bg-amber-500',
          secondary: 'bg-amber-300'
        };
      default:
        return {
          primary: 'bg-primary',
          secondary: 'bg-emerald-300'
        };
    }
  };
  const blurColors = getBlurColors();
  return <section className={`relative overflow-hidden min-h-[85vh] flex items-center ${getBackgroundClasses()}`}>
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className={`absolute top-1/4 left-1/4 w-96 h-96 ${blurColors.primary} rounded-full blur-3xl`}></div>
        <div className={`absolute bottom-1/3 right-1/4 w-64 h-64 ${blurColors.secondary} rounded-full blur-2xl`}></div>
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Colonne gauche - Contenu */}
          <ScrollReveal>
            <div className="space-y-8">
              <div className="space-y-6">
                {badge && <Badge className="bg-primary/10 text-primary border-primary/20 px-4 py-2">
                    {badge.icon && <span className="mr-2">{badge.icon}</span>}
                    {badge.text}
                  </Badge>}
                
                <h1 className="text-5xl lg:text-6xl font-bold leading-tight text-slate-900 xl:text-5xl">
                  {title}
                  {titleHighlight && <span className="block text-primary">{titleHighlight}</span>}
                </h1>
                
                <p className="text-xl text-slate-700 leading-relaxed max-w-xl xl:text-base">
                  {description}
                </p>
              </div>
              
              {/* Bénéfices optionnels */}
              {benefits && benefits.length > 0 && <div className="space-y-3">
                  {benefits.map((benefit, index) => <div key={index} className="flex items-center">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center mr-3">
                        <benefit.icon className="h-4 w-4 text-primary" />
                      </div>
                      <span className="text-slate-700">{benefit.text}</span>
                    </div>)}
                </div>}
              
              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-white px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 group">
                  <Link to={primaryCTA.href} className="inline-flex items-center justify-center gap-2 whitespace-nowrap ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 h-11 rounded-md bg-primary hover:bg-primary/90 text-white px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 group xl:text-base">
                    {primaryCTA.text}
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
                
                {secondaryCTA && <Button asChild variant="outline" size="lg" className="border-primary/30 text-primary hover:bg-primary/5 px-8 py-4 text-lg">
                    <Link to={secondaryCTA.href} className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border bg-transparent hover:text-accent-foreground h-11 rounded-md border-primary/30 text-primary hover:bg-primary/5 px-8 py-4 text-lg xl:text-base">
                      {secondaryCTA.text}
                    </Link>
                  </Button>}
              </div>
            </div>
          </ScrollReveal>
          
          {/* Colonne droite - Visuel */}
          <ScrollReveal delay={300}>
            <div className="flex items-center justify-center">
              <div className="relative bg-white/60 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50">
                <img src={image.src} alt={image.alt} className="w-full max-w-sm hover:scale-105 transition-all duration-500 h-[210px] object-cover" />
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>;
};
export default Hero;